import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Check, Copy, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface SendResult {
  tracking_code: string;
  tracking_url: string;
  tracking_expires_at: string;
  is_existing_user: boolean;
}

interface SuccessStepProps {
  sendResult: SendResult;
  recipientName: string;
  onReset: () => void;
}

const SuccessStep: React.FC<SuccessStepProps> = ({ sendResult, recipientName, onReset }) => {
  const { t } = useTranslation('quicksend');
  const navigate = useNavigate();
  const [linkCopied, setLinkCopied] = useState(false);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(sendResult.tracking_url);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  return (
    <div className="max-w-lg mx-auto text-center">
      <div className="w-20 h-20 bg-green/10 rounded-full flex items-center justify-center mx-auto mb-5 border-2 border-green/20">
        <Check className="h-10 w-10 text-green" />
      </div>

      <h2 className="text-2xl font-bold text-navy mb-2">{t('success_title')}</h2>
      <p className="text-gray-500 mb-8">
        {t('success_description', { name: recipientName })}
      </p>

      {sendResult.is_existing_user ? (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
          <h3 className="font-semibold text-navy mb-2">{t('success_existing_user_title')}</h3>
          <p className="text-sm text-gray-500 mb-4">{t('success_existing_user_description')}</p>
          <Button onClick={() => navigate('/login')} className="w-full">
            {t('success_sign_in')}
            <ExternalLink className="h-4 w-4 ml-2" />
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
          <h3 className="font-semibold text-navy mb-2">{t('success_tracking_title')}</h3>
          <p className="text-sm text-gray-500 mb-4">{t('success_tracking_description')}</p>

          <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-3 mb-4">
            <code className="text-xs text-gray-700 flex-1 break-all">
              {sendResult.tracking_url}
            </code>
            <Button variant="outline" size="sm" onClick={handleCopyLink}>
              {linkCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              <span className="ml-1 text-xs">
                {linkCopied ? t('success_link_copied') : t('success_copy_link')}
              </span>
            </Button>
          </div>

          <Button onClick={() => navigate('/join')} variant="default" className="w-full mb-2 bg-green hover:bg-green-dark text-white">
            {t('success_tracking_cta')}
          </Button>
        </div>
      )}

      <Button variant="ghost" onClick={onReset}>
        {t('success_send_another')}
      </Button>
    </div>
  );
};

export default SuccessStep;
